import React, { useState, useEffect, useCallback } from "react";
import {Button, Card, Input, InputIcon, ProgressRing, Textarea} from "@salesforce/design-system-react";
import {fetchAccountMigrationStatus, approveAccount, rejectAccount} from "../../../../services/accountMigration";
import {CONSTANT_ACCOUNT_MIGRATION} from "../../../../constants/pcsk_admin/accountMigration";
import CustomSpinnerOverlay from "../../../../utils/components/spinner/custom_spinner_overlay";
import {ToastState} from "../../../../utils/types/toast";

type AccountMigrationStatusProps = {
    isActive: boolean;
    setToast: React.Dispatch<React.SetStateAction<ToastState>>;
};

const AccountMigrationStatus: React.FC<AccountMigrationStatusProps> = ({ isActive, setToast }) => {
    const [isSearchRequestButtonDisabled, setIsSearchRequestButtonDisabled] = useState(true);
    const [searchRequestErrorMessage, setSearchRequestErrorMessage] = useState("");
    const [showSpinner, setShowSpinner] = useState(false);
    const [accountIdInput, setAccountIdInput] = useState("");
    const [currentProgressStep, setCurrentProgressStep] = useState(0);
    const [accountMigrationStatus, setAccountMigrationStatus] = useState<Array<{
        account_id: string;
        overall_status: string;
        current_step: string;
        progress_step: number;
    }>>([]);
    const [apiResponse, setApiResponse] = useState<any>(null);
    const [pendingNoRecordToast, setPendingNoRecordToast] = useState<string | null>(null);

    const clearSelections = () => {
        setAccountMigrationStatus([])
        setCurrentProgressStep(0);
        setApiResponse(null);
        setPendingNoRecordToast(null);
    };

    useEffect(() => {
        if (isActive) {
            clearSelections();
            setAccountIdInput("");
        }
    }, [isActive]);

    // Handle pending no record toast outside of error context
    useEffect(() => {
        if (pendingNoRecordToast) {
            setToast({
                isVisible: true,
                message: {
                    heading: "No Migration Record Found",
                    details: `No migration record exists for account ${pendingNoRecordToast}. This account has not been submitted for migration yet.`
                },
                variant: "info",
            });
            setPendingNoRecordToast(null); // Clear the flag
        }
    }, [pendingNoRecordToast, setToast]);

    const validateAccountId = useCallback(() => {
        if (accountIdInput.trim() === "") {
            setSearchRequestErrorMessage("");
            return false;
        }
        // Check if it's a 12-digit whole number
        const accountIDRegex = /^\d{12}$/;
        if (!accountIDRegex.test(accountIdInput)) {
            setSearchRequestErrorMessage("Account ID must be a 12-digit whole number");
            return false;
        }
        setSearchRequestErrorMessage("");
        return true;
    }, [accountIdInput]);

    useEffect(() => {
        setIsSearchRequestButtonDisabled(!validateAccountId());
    }, [validateAccountId]);

    const handleSearchAccountId = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAccountIdInput(event.target.value)
    };

    // Progress stages configuration
    const progressStages: { label: string; value: string; statusField: string }[] = [
        { label: CONSTANT_ACCOUNT_MIGRATION.ROLLBACK, value: 'rollback', statusField: 'rollback_status' },
        { label: CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_MOVEMENT, value: 'mpa_move', statusField: 'mpa_move_status' },
        { label: CONSTANT_ACCOUNT_MIGRATION.WAIT_FOR_APPROVAL, value: 'manual_approval', statusField: 'approval_status' },
        { label: CONSTANT_ACCOUNT_MIGRATION.PROVISIONING, value: 'provisioning', statusField: 'provision_status' }
    ];

    // Helper function to get step status display information
    const getStepStatusInfo = (statusValue: string) => {
        switch (statusValue) {
            case 'SUCCESS':
                return {
                    color: '#28a745', // Green
                    displayText: 'Completed',
                    showCheckmark: true,
                    showDot: false
                };
            case 'FAILED':
                return {
                    color: '#dc3545', // Red
                    displayText: 'Failed',
                    showCheckmark: false,
                    showDot: false
                };
            case 'IN_PROGRESS':
            case 'PROCESSING':
                return {
                    color: '#0176d3', // Blue
                    displayText: 'In Progress',
                    showCheckmark: false,
                    showDot: true
                };
            default:
                return {
                    color: '#dee2e6', // Gray
                    displayText: 'Pending',
                    showCheckmark: false,
                    showDot: false
                };
        }
    };

    const handleApproval = async (action: 'approve' | 'reject', accountId: string) => {
        try {
            setShowSpinner(true);
            
            if (action === 'approve') {
                const response = await approveAccount(accountId);
                console.log('Approval response:', response);
                
                setToast({
                    isVisible: true,
                    message: {
                        heading: "Account Approved",
                        details: `Account ${accountId} has been approved successfully for migration`
                    },
                    variant: "success",
                });
                // Refresh the status after approval
                await handleFetchRequestStatus();
            } else {
                // TODO: Implement reject functionality when API is ready
                const response = await rejectAccount(accountId);
                console.log('Rejection response:', response);
                setToast({
                    isVisible: true,
                    message: {
                        heading: "Account Rejected", 
                        details: `Account ${accountId} has been rejected for migration`
                    },
                    variant: "error",
                });
                // Refresh the status after rejection
                await handleFetchRequestStatus();
            }
        } catch (error) {
            console.error(`Error ${action}ing account:`, error);
            setToast({
                isVisible: true,
                message: {
                    heading: `Error ${action}ing account`,
                    details: (error as Error)?.message || `Failed to ${action} account. Please try again.`
                },
                variant: "error",
            });
        } finally {
            setShowSpinner(false);
        }
    };

    const handleFetchRequestStatus = async () => {
        setShowSpinner(true);
        clearSelections();
        try {
            const response = await fetchAccountMigrationStatus(accountIdInput);
            console.log("Received response:", response);

            // Check if the response indicates no record found (successful response but no data)
            
            if (response.message && response.message.toLowerCase().includes("no record found")) {
                setAccountMigrationStatus([]);
                setApiResponse(null);
                setToast({
                    isVisible: true,
                    message: {
                        heading: "No Migration Record Found",
                        details: `No migration record exists for account ${accountIdInput}. This account has not been submitted for migration yet.`
                    },
                    variant: "warning",
                });
                return;
            }

            // Handle the response from API
            if (response.current_step) {
                const progressIndex = progressStages.findIndex(stage => stage.value === response.current_step);
                const validProgressIndex = progressIndex === -1 ? 0 : progressIndex;
                
                const statuses = [{
                    account_id: accountIdInput,
                    overall_status: response.overall_status,
                    current_step: response.current_step,
                    progress_step: validProgressIndex
                }];

                
                setAccountMigrationStatus(statuses as { account_id: string; overall_status: string; current_step: string; progress_step: number }[]);
                setCurrentProgressStep(validProgressIndex);
                setApiResponse(response);
            } else {
                // If no current_step, check if it's a different "no record" format
                setAccountMigrationStatus([]);
                setApiResponse(null);
                setToast({
                    isVisible: true,
                    message: {
                        heading: "No Migration Record Found",
                        details: `No migration record exists for account ${accountIdInput}. This account has not been submitted for migration yet.`
                    },
                    variant: "info",
                });
            }
        } catch (error) {
            console.error("Error in handleFetchRequestStatus:", error);
            setAccountMigrationStatus([]);
            setApiResponse(null);
            
            const errorMessage = (error as Error)?.message || "No status found. Please get in touch with PCSK team if the issue persists";
            
            // Check if it's a "no record found" error - show as warning instead of error
            // Check for various possible "no record" message formats
            const noRecordIndicators = [
                "no record found",
                "record not found", 
                "no record present",
                "account not found",
                "migration record not found",
                "404",
                "not found"
            ];
            
            const isNoRecordError = noRecordIndicators.some(indicator => 
                errorMessage.toLowerCase().includes(indicator)
            );
            
            if (isNoRecordError) {
                setPendingNoRecordToast(accountIdInput);
            } else {
                setToast({
                    isVisible: true,
                    message: {
                        heading: "Error fetching status",
                        details: errorMessage
                    },
                    variant: "error",
                });
            }
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <Card heading={CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_MIGRATION_STATUS}>
            <div className="slds-p-around_small">
                <div>
                    <Input
                        id="account_migration_id"
                        label={CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_ID}
                        required
                        value={accountIdInput}
                        onChange={handleSearchAccountId}
                        errorText={searchRequestErrorMessage}
                        placeholder={CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_ID_PLACEHOLDER}
                        iconLeft={<InputIcon name="search" category="utility" />}
                    />
                </div>
                <div className="slds-p-top_medium slds-grid slds-grid_vertical-align-center">
                    <Button
                        label="Check Status"
                        variant="brand"
                        onClick={handleFetchRequestStatus}
                        disabled={isSearchRequestButtonDisabled}
                    />
                    {showSpinner && (
                        <div className="slds-m-left_medium">
                            <CustomSpinnerOverlay size={"small"}/>
                        </div>
                    )}
                </div>
                {/* Always show timeline - gets updated when Check Status is clicked */}
                <div className="slds-p-top_medium">
                    <div className="slds-m-bottom_small">
                        <h3 className="slds-text-heading_small">Migration Progress</h3>
                    </div>
                    <div className="slds-p-around_small" style={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e5e5e5',
                        borderRadius: '6px'
                    }}>
                        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {progressStages.map((stage, index) => {
                                const hasData = accountMigrationStatus.length > 0 && apiResponse;
                                const isLast = index === progressStages.length - 1;
                                
                                // Get individual step status from API response
                                let stepStatus = 'PENDING';
                                if (hasData && apiResponse[stage.statusField]) {
                                    stepStatus = apiResponse[stage.statusField];
                                }
                                
                                const statusInfo = getStepStatusInfo(stepStatus);
                                
                                
                                return (
                                    <li key={stage.value} style={{ 
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        paddingBottom: isLast ? '0' : '1rem'
                                    }}>
                                        {/* Timeline marker - fixed position */}
                                        <div style={{ 
                                            position: 'relative', 
                                            zIndex: 2,
                                            width: '1.5rem',
                                            height: '1.5rem',
                                            flexShrink: 0
                                        }}>
                                            <div 
                                                style={{
                                                    width: '1.5rem',
                                                    height: '1.5rem',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: statusInfo.color,
                                                    border: '2px solid #fff',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }}
                                            >
                                                {statusInfo.showCheckmark && (
                                                    <svg className="slds-icon slds-icon_xx-small" style={{ fill: 'white' }} aria-hidden="true">
                                                        <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#check"></use>
                                                    </svg>
                                                )}
                                                {statusInfo.showDot && (
                                                    <div style={{
                                                        width: '6px',
                                                        height: '6px',
                                                        backgroundColor: 'white',
                                                        borderRadius: '50%'
                                                    }}></div>
                                                )}
                                                {stepStatus === 'FAILED' && (
                                                    <svg className="slds-icon slds-icon_xx-small" style={{ fill: 'white' }} aria-hidden="true">
                                                        <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#close"></use>
                                                    </svg>
                                                )}
                                            </div>
                                            {/* Connecting line */}
                                            {!isLast && (
                                                <div 
                                                    style={{
                                                        position: 'absolute',
                                                        top: '1.5rem',
                                                        left: '0.75rem',
                                                        transform: 'translateX(-50%)',
                                                        width: '2px',
                                                        height: '1rem',
                                                        backgroundColor: stepStatus === 'SUCCESS' ? '#28a745' : '#dee2e6',
                                                        zIndex: 1
                                                    }}
                                                ></div>
                                            )}
                                        </div>
                                        {/* Step content - left aligned */}
                                        <div style={{ 
                                            marginLeft: '1rem',
                                            flex: 1
                                        }}>
                                            <div className="slds-text-body_regular" style={{
                                                color: statusInfo.color === '#dee2e6' ? '#6c757d' : '#212529',
                                                fontWeight: stepStatus === 'IN_PROGRESS' || stepStatus === 'PROCESSING' ? '600' : '400'
                                            }}>
                                                {stage.label}
                                            </div>
                                            <div className="slds-text-body_small" style={{
                                                color: statusInfo.color
                                            }}>
                                                {statusInfo.displayText}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </div>
                
                {/* Show account status details only after search */}
                {accountMigrationStatus.length > 1 ? (
                    <div className="slds-p-top--medium">
                        <p>Multiple results found. Details will be shown in a modal.</p>
                    </div>
                ) : accountMigrationStatus.length === 1 ? (
                    (() => {
                        const request = accountMigrationStatus[0]; // Get the first result
                        return (
                            <>
                                {(request.overall_status === "IN_PROGRESS" || request.overall_status === "PROCESSING") && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="warning"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.PROCESSING}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {request.overall_status === "SUCCESS" && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="base"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.SUCCESS}
                                            </div>
                                        </div>
                                        <Input label={CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_ID} value={request.account_id} disabled/>
                                    </div>
                                )}
                                {request.overall_status === "FAILED" && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="expired"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.FAILURE}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {(request.overall_status === "NOT_FOUND" || request.overall_status === "not found") && (
                                    <div className="slds-p-top--medium">
                                        <div className="slds-grid">
                                            <div className="slds-m-left--x-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_STATUS}:
                                            </div>
                                            <div className="slds-m-left--x-small">
                                                <ProgressRing value={100} theme="expired"/>
                                            </div>
                                            <div className="slds-m-left--xx-small slds-m-top--xxx-small">
                                                {CONSTANT_ACCOUNT_MIGRATION.NOT_FOUND}
                                            </div>
                                        </div>
                                        {request.account_id?.length !== 0 && <Input label={CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_ID} value={request.account_id} disabled/>}
                                    </div>
                                )}
                                {/* Add Approval Buttons */}
                                {accountMigrationStatus.length > 0 && request.current_step === 'manual_approval' && (
                                    <div className="slds-p-top_medium">
                                        <div className="slds-grid slds-grid_vertical-align-center">
                                            <Button
                                                label={CONSTANT_ACCOUNT_MIGRATION.APPROVE_BUTTON}
                                                variant="brand"
                                                onClick={() => handleApproval('approve', request.account_id)}
                                            />
                                            <div className="slds-m-left_small">
                                                <Button
                                                    label={CONSTANT_ACCOUNT_MIGRATION.REJECT_BUTTON}
                                                    variant="destructive"
                                                    onClick={() => handleApproval('reject', request.account_id)}
                                                />
                                            </div>
                                        </div>
                                        <div className="slds-p-top_x-small">
                                            <div className="slds-text-body_small slds-text-color_weak">
                                                Approve or reject the migration to proceed to the next step.
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    })()
                ) : null}
            </div>
        </Card>
    );
};

export default AccountMigrationStatus;
